// Same-folder imports with `.ts`: the Node test runner can't resolve `@/`.
import {
  isBusinessEmailRequiredError,
  isInvalidTrialOfferError,
} from './constants.ts';
import type { AuthIntent } from './types/externalLogin.ts';

/**
 * Why an OAuth callback (Google, Slack, LinkedIn) didn't sign the user in,
 * which picks the callback screen's heading, copy and actions.
 */
export type OAuthFailureKind =
  /** The user backed out at the provider. Not an error. */
  | 'cancelled'
  /** The API refused a personal email. */
  | 'businessEmail'
  /** The API refused the sign-up link's trial offer. */
  | 'trialOffer'
  /** The stored sign-in is gone or doesn't match: too old, or another browser. */
  | 'expired'
  | 'failed';

/** What the browser stored when the sign-in started, so the callback can restart it. */
export interface OAuthSignInRequest {
  returnTo: string;
  acceptTerms: boolean;
  intent: AuthIntent;
}

/** Provider error codes meaning the stored sign-in is missing or doesn't match. */
const expiredCodes = new Set([
  'missing_state',
  'invalid_state',
  'invalid_state_data',
  'invalid_nonce',
]);

export const classifyOAuthFailure = ({
  error,
  ssoErrorCode,
  trialOfferCode,
}: {
  error: unknown;
  /** The provider module's error code (`GoogleSsoError.code` etc.), if it threw one. */
  ssoErrorCode?: string;
  trialOfferCode?: string;
}): OAuthFailureKind => {
  if (ssoErrorCode === 'cancelled') return 'cancelled';
  if (trialOfferCode && isInvalidTrialOfferError(error)) return 'trialOffer';
  if (isBusinessEmailRequiredError(error)) return 'businessEmail';
  if (ssoErrorCode && expiredCodes.has(ssoErrorCode)) return 'expired';
  return 'failed';
};
